<template>
  <div class="pa-layout">
    <div class="pa-layout__back-row">
      <router-link to="/sites" class="pa-layout__back-link">
        <i class="material-icons">arrow_back</i>
        <span>To All Sites</span>
      </router-link>
      <white-glove-admin-mode-toggle />
    </div>

    <!-- Wait for the first statuses load so a site in setup never flashes the tabs first -->
    <div v-if="!ordersLoadedOnce" class="pa-layout__loading mainColor--text">
      Loading…
    </div>

    <!-- Setup phase: no site name, no tabs, the setup wizard inline (design: project view "inSetup") -->
    <setup-wizard-host
      v-else-if="showSetupWizard"
      :key="siteId"
      :site="site"
      :status="siteProjectStatus"
      @audits-changed="loadWhiteGloveOrders"
    />

    <template v-else>
      <div class="pa-layout__title-row">
        <h1 class="pa-layout__site-name">
          {{ site.name }}
          <span
            v-if="site && site.name"
            class="wg-status-dot pa-layout__site-status"
            :style="siteStatusDotStyle"
            :title="siteStatusInsight.statusLabel"
            role="img"
            :aria-label="siteStatusInsight.statusLabel ? `Status: ${siteStatusInsight.statusLabel}` : 'Site status'"
          ></span>
        </h1>
      </div>

      <nav class="pa-layout__tabs">
        <router-link
          v-for="tab in tabs"
          :key="tab.name"
          :to="{ name: tab.name, params: { siteId: $route.params.siteId } }"
          class="pa-layout__tab"
          :class="{ 'pa-layout__tab--active': isTabActive(tab.name) }"
        >
          {{ tab.label }}
          <span
            v-if="tab.name === 'AllContentWhiteGlove' && pendingApprovals > 0"
            class="pa-pulse"
            :title="`${pendingApprovals} article${pendingApprovals === 1 ? '' : 's'} waiting for your approval`"
          ></span>
          <span
            v-if="tab.name === POP_AUTHORITY_BOOSTERS_ROUTE && showBoostersNewPill"
            class="pa-layout__tab-new"
            aria-label="New"
          >NEW</span>
        </router-link>
      </nav>

      <router-view />
    </template>

    <async-tasks-popup
      v-if="siteId"
      :site-id="siteId"
      :campaign-id="domainLevelCampaign ? domainLevelCampaign.id : 0"
    />

    <!-- the design's confirmation toast, shared by every POP Authority screen under this layout -->
    <pa-toast />
  </div>
</template>

<script>
import AsyncTasksPopup from '@/components/AsyncTasksPopup.vue'
import { mixin } from '@/components/mixin'
import {
  getWhiteGloveSiteCardInsight,
  parseWhiteGloveOrdersResponse,
  whiteGloveStatusDotStyle,
} from '@/utils/whiteGloveSiteList'
import {
  POP_AUTHORITY_BOOSTERS_ROUTE,
  popAuthoritySiteInSetup,
  popAuthoritySiteStatus,
} from '@/utils/popAuthority'
import WhiteGloveAdminModeToggle from '@/components/SitesSingleView/WhiteGlove/WhiteGloveAdminModeToggle.vue'
import PaToast from '@/components/SitesSingleView/PopAuthority/PaToast.vue'
import SetupWizardHost from '@/components/SitesSingleView/PopAuthority/SetupWizardHost.vue'

/**
 * POP Authority single-site layout (design: pa-app.jsx project view).
 * Route names are shared with White Glove; the route-level family switch renders the POP Authority
 * screen behind each name (Overview, Content plans, Deliverables, and the Wins / Settings wrappers
 * around the White Glove tabs), Boosters is a POP Authority only route, and Order history reuses
 * the White Glove component as is.
 */
export default {
  name: 'PopAuthoritySitesSingleLayout',
  components: { AsyncTasksPopup, PaToast, SetupWizardHost, WhiteGloveAdminModeToggle },
  mixins: [mixin],
  provide () {
    return {
      refreshWhiteGloveOrders: () => this.loadWhiteGloveOrders(),
      // Current status row of the site (package, cycle, CTAs) for the plans list / plan detail
      getPopAuthoritySiteStatus: () => this.siteProjectStatus,
    }
  },
  data () {
    return {
      POP_AUTHORITY_BOOSTERS_ROUTE,
      ordersLoading: true,
      // First statuses load finished (stays true on later refreshes so the page never blanks)
      ordersLoadedOnce: false,
      whiteGloveOrders: [],
      whiteGloveSiteProjectStatuses: {},
      campaigns: [],
    }
  },
  computed: {
    siteId () {
      const id = parseInt(this.$route.params.siteId, 10)
      return Number.isFinite(id) ? id : null
    },
    domainLevelCampaigns () {
      return this.campaigns.filter((item) => item.isDomainLevelCampaign)
    },
    domainLevelCampaign () {
      return this.domainLevelCampaigns.length > 0 ? this.domainLevelCampaigns[0] : null
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isWhiteGloveAdminMode () {
      return this.$store.state.isWhiteGloveAdminMode
    },
    whiteGloveMissionControlAdminUi () {
      return this.isMissionControlSession && this.isWhiteGloveAdminMode
    },
    isWhiteGloveFamilyUser () {
      return this.$store.getters.isWhiteGloveFamilyUser
    },
    isPopAuthorityUser () {
      return this.$store.getters.isPopAuthorityUser
    },
    tabs () {
      const t = [
        { label: 'Overview', name: 'OverviewWhiteGlove' },
        { label: 'Content plans', name: 'ContentPlansWhiteGlove' },
        { label: 'Deliverables', name: 'AllContentWhiteGlove' },
        { label: 'Wins', name: 'WinsWhiteGlove' },
        { label: 'Order history', name: 'OrderHistoryWhiteGlove' },
        { label: 'Boosters', name: POP_AUTHORITY_BOOSTERS_ROUTE },
        { label: 'Settings', name: 'SettingsWhiteGlove' },
      ]
      if (this.whiteGloveMissionControlAdminUi) {
        t.push({ label: 'SEO Tools', name: 'SeoToolsWhiteGlove' })
      }
      return t
    },
    /** This site's row from `siteProjectStatuses` (status key, copy, timeline, exception), or null. */
    siteProjectStatus () {
      return popAuthoritySiteStatus(this.siteId, this.whiteGloveSiteProjectStatuses)
    },
    /** Delivered articles still waiting for the client's approval: pulse on the Deliverables tab. */
    pendingApprovals () {
      const status = this.siteProjectStatus
      return Number(status && status.pending_approvals) || 0
    },
    /** "NEW" on the Boosters tab until the project has its first booster order (self-retiring). */
    showBoostersNewPill () {
      const boosters = this.siteProjectStatus && this.siteProjectStatus.boosters
      if (Array.isArray(boosters)) return boosters.length === 0
      return !this.whiteGloveOrders.some(
        (o) => o.type === 'booster_order' && Number(o.site_id) === this.siteId
      )
    },
    /** Setup phase (setup fee, site form, publishing, Google step): wizard instead of tabs. */
    showSetupWizard () {
      return (
        this.isPopAuthorityUser &&
        this.ordersLoadedOnce &&
        popAuthoritySiteInSetup(this.siteId, this.whiteGloveOrders, this.whiteGloveSiteProjectStatuses)
      )
    },
    siteStatusInsight () {
      const id = this.site && this.site.id
      if (!id) {
        return { dotColor: '#9e9e9e', statusLabel: '' }
      }
      return getWhiteGloveSiteCardInsight(id, this.whiteGloveOrders, this.whiteGloveSiteProjectStatuses)
    },
    siteStatusDotStyle () {
      return whiteGloveStatusDotStyle(this.siteStatusInsight.dotColor)
    },
    /** Stable id for watch; omitting subAccountRelationId on /api/sites/ is valid (owner's sites). */
    whiteGloveSubAccountRelationId () {
      const r = this.$store.state.selectedSubAccountRelation
      if (r && typeof r === 'object' && r.id != null) return r.id
      return null
    },
  },
  watch: {
    whiteGloveSubAccountRelationId: {
      handler (newId, oldId) {
        if (newId === oldId) return
        this.ensureSitesLoaded().then(() => {
          this.loadWhiteGloveOrders()
          this.loadCampaigns()
          this.ensureSeoToolsRouteAllowed()
        })
      },
      immediate: true,
    },
    '$route.params.siteId': {
      handler () {
        this.ensureSitesLoaded().then(() => {
          this.loadWhiteGloveOrders()
          this.loadCampaigns()
        })
      },
      immediate: false,
    },
    isWhiteGloveFamilyUser (v) {
      if (v) {
        this.ensureSitesLoaded().then(() => {
          this.loadWhiteGloveOrders()
          this.loadCampaigns()
        })
      } else {
        this.whiteGloveOrders = []
        this.whiteGloveSiteProjectStatuses = {}
        this.campaigns = []
      }
    },
    '$route.name' () {
      this.ensureSeoToolsRouteAllowed()
    },
    isWhiteGloveAdminMode () {
      this.ensureSeoToolsRouteAllowed()
    },
    isMissionControlSession () {
      this.ensureSeoToolsRouteAllowed()
    },
  },
  methods: {
    ensureSitesLoaded () {
      const siteId = parseInt(this.$route.params.siteId, 10)
      const hasSite = Number.isFinite(siteId) && this.$store.getters.siteById(siteId)
      if (this.$store.state.sites.length && hasSite) {
        return Promise.resolve()
      }
      return this.$store
        .dispatch('loadSites')
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.$store.commit('setSites', response.data.sites)
          }
        })
        .then(() => {
          if (!Number.isFinite(siteId) || this.$store.getters.siteById(siteId)) {
            return
          }
          return this.$store.dispatch('loadSite', siteId).then((res) => {
            if (res.data && res.data.status === 'SUCCESS' && res.data.site) {
              this.$store.commit('setSite', res.data.site)
            }
          })
        })
    },
    ensureSeoToolsRouteAllowed () {
      if (this.$route.name !== 'SeoToolsWhiteGlove') return
      if (this.whiteGloveMissionControlAdminUi) return
      const siteId = this.$route.params.siteId
      if (siteId == null || siteId === '') return
      this.$router.replace({ name: 'OverviewWhiteGlove', params: { siteId: String(siteId) } })
    },
    isTabActive (tabName) {
      if (this.$route.name === tabName) return true
      if (tabName === 'ContentPlansWhiteGlove' && this.$route.name === 'ContentPlanDetailWhiteGlove') {
        return true
      }
      if (tabName === 'AllContentWhiteGlove' && this.$route.name === 'ContentOnlyDetailWhiteGlove') {
        return true
      }
      return false
    },
    loadCampaigns () {
      if (!this.siteId) {
        this.campaigns = []
        return Promise.resolve()
      }
      return this.$store.dispatch('getCampaignsForSite', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.campaigns = response.data.campaigns || []
          }
        })
    },
    loadWhiteGloveOrders () {
      if (!this.isWhiteGloveFamilyUser) {
        this.whiteGloveOrders = []
        this.whiteGloveSiteProjectStatuses = {}
        this.ordersLoading = false
        this.ordersLoadedOnce = true
        return Promise.resolve()
      }
      this.ordersLoading = true
      return this.$store
        .dispatch('getWhiteGloveOrders')
        .then((response) => {
          const parsed = parseWhiteGloveOrdersResponse(response)
          this.whiteGloveOrders = parsed.orders
          this.whiteGloveSiteProjectStatuses = parsed.siteProjectStatuses
        })
        .catch(() => {
          this.whiteGloveOrders = []
          this.whiteGloveSiteProjectStatuses = {}
        })
        .finally(() => {
          this.ordersLoading = false
          this.ordersLoadedOnce = true
        })
    },
  },
}
</script>

<style lang="scss" scoped>
/* Design tokens (pa.css) mapped to the Vuetify theme: teal = mainColor, accent = profileAnchorColor,
   border-soft = gray15Color, ink-strong = mainColor3. */
@import './setup/setup.scss';

.pa-layout {
  padding: 20px 20px 0;

  &__back-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
  }

  &__back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--v-mainColor-base);
    font-size: 0.9rem;
    font-weight: 700;
    text-decoration: none;

    .material-icons {
      font-size: 18px;
    }

    &:hover {
      color: var(--v-profileAnchorColor-base);
    }
  }

  &__loading {
    font-size: 0.95rem;
    margin: 16px 0;
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 2px 0 14px;
  }

  &__site-name {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 1.7rem;
    font-weight: 800;
    color: var(--v-mainColor-base);
    margin: 0;
  }

  &__site-status {
    flex-shrink: 0;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06);
  }

  /* design pa.css .pa-tabs: no overflow rule; the tabs overlap the 1.5px baseline by design, and any
     overflow value other than visible turns that overlap into a vertical scrollbar */
  &__tabs {
    display: flex;
    gap: 26px;
    border-bottom: 1.5px solid var(--v-gray15Color-base, #e4e4e4);
    margin-bottom: 22px;

    @media (max-width: 760px) {
      flex-wrap: wrap;
      row-gap: 0;
    }
  }

  &__tab {
    display: inline-flex;
    align-items: center;
    padding: 10px 2px;
    font-size: 1rem;
    font-weight: 700;
    color: var(--v-mainColor-base);
    text-decoration: none;
    white-space: nowrap;
    border-bottom: 3px solid transparent;
    margin-bottom: -1.5px;
    transition: color 0.15s, border-color 0.15s;

    &:hover {
      color: var(--v-profileAnchorColor-base);
    }

    &--active {
      color: var(--v-mainColor3-base, #171717);
      border-bottom-color: var(--v-profileAnchorColor-base);
    }
  }

  &__tab-new {
    background: $pa-accent;
    color: #fff;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    border-radius: 999px;
    padding: 2px 7px;
    margin-left: 6px;
    line-height: 1.2;
  }
}
</style>
